import { Link } from "react-router-dom";
import { ArrowRight, Image as ImageIcon, Search, MousePointerClick, CalendarCheck, CheckCircle } from "lucide-react";
import { SectionHeading } from "../../components/shared/SectionHeading";
import { CATEGORIES } from "../../data/mockData";
import { usePageMedia } from "../../lib/usePageMedia";

const STEPS = [
  { icon: Search, title: "Explorez", text: "Découvrez notre collection librement, sans compte ni date obligatoire." },
  { icon: MousePointerClick, title: "Composez", text: "Ajoutez vos articles préférés à votre sélection." },
  { icon: CalendarCheck, title: "Indiquez votre date", text: "Consultez les disponibilités au moment qui vous convient." },
  { icon: CheckCircle, title: "Envoyez votre demande", text: "Art Event vérifie et valide. L'acompte de 30 % confirme ensuite votre réservation." },
];

export default function BoutiquePage() {
  const pageMedia = usePageMedia();
  return (
    <div data-testid="boutique-page">
      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="container-ae">
          <p className="overline-ae mb-3">Location</p>
          <h1 className="max-w-3xl text-4xl sm:text-5xl lg:text-6xl tracking-tight text-deepgreen" data-testid="boutique-title">
            La collection <span className="font-accent text-sage">Art Event</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            Décoration, mobilier et jeux en bois à louer pour votre événement. Consultez librement la boutique — la date ne sera demandée qu'au moment de vérifier les disponibilités.
          </p>
        </div>
      </section>

      <section className="pb-20" data-testid="boutique-categories">
        <div className="container-ae">
          <div className="mt-10 grid grid-cols-2 gap-5 lg:grid-cols-4">
            {CATEGORIES.map((c) => {
              const slot = pageMedia[`boutique:categorie:${c.slug}`];
              return (
              <Link
                key={c.slug}
                to={`/boutique/${c.slug}`}
                data-testid={`category-card-${c.slug}`}
                className="group relative overflow-hidden rounded-xl"
              >
                {slot ? (
                  <img src={slot.url} alt={slot.alt || c.name} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid={`category-image-${c.slug}`} />
                ) : (
                  <div className="flex aspect-square w-full items-center justify-center bg-sage-light" data-testid={`category-image-${c.slug}`}>
                    <ImageIcon className="h-9 w-9 text-deepgreen/40" aria-hidden />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-deepgreen/75 via-deepgreen/15 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-5">
                  <h3 className="font-heading text-lg text-white">{c.name}</h3>
                  <ArrowRight className="h-4 w-4 text-gold-light transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
              );
            })}
          </div>
          <div className="mt-14 rounded-xl bg-white p-8 text-center md:p-10" data-testid="boutique-help">
            <h2 className="font-heading text-2xl text-deepgreen">Vous ne savez pas encore quoi choisir ?</h2>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <Link to="/boutique/tous" data-testid="boutique-all-products" className="btn-primary-ae">Voir tous les articles</Link>
              <Link to="/realisations" data-testid="boutique-see-realisations" className="btn-secondary-ae">Voir nos réalisations</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-sage-light py-20 md:py-24" data-testid="boutique-how-it-works">
        <div className="container-ae">
          <SectionHeading overline="Simple & sécurisé" title="Comment fonctionne la location ?" align="center" />
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative text-center" data-testid={`step-${i + 1}`}>
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-deepgreen text-cream">
                  <s.icon className="h-6 w-6" aria-hidden />
                </div>
                <p className="mt-4 font-accent text-2xl text-sage">Étape {i + 1}</p>
                <h3 className="mt-1 font-heading text-xl uppercase tracking-wide text-deepgreen">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
